import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Mic, Loader2, CheckCircle, AlertCircle, Square, X, Download } from 'lucide-react';
import { useRecorderStore } from '../../stores/recorderStore';

const formatDuration = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

export const GlobalRecordingIndicator: React.FC = () => {
  const navigate = useNavigate();
  const recorderStore = useRecorderStore();

  const [showCompleted, setShowCompleted] = useState(false);
  const [countdown, setCountdown] = useState<number | null>(null);
  const [exported, setExported] = useState(false);

  const {
    isRecording,
    currentCandidateId,
    currentStage,
    candidateName,
    duration,
    processingStatus,
    uploadErrorKind,
    uploadFailures,
    savedDuration,
    alertAcknowledged,
  } = recorderStore;

  // 上传通道异常时不再展示"录音中"，否则界面等于在骗人
  const hasUploadError = uploadErrorKind !== null;
  // 模态只在用户尚未确认时弹出；确认后降级为顶部徽标常驻
  const showAlertModal = hasUploadError && !alertAcknowledged;

  // 当处理完成时显示并倒计时
  useEffect(() => {
    if (processingStatus === 'completed') {
      setShowCompleted(true);
      setCountdown(5);
    }
  }, [processingStatus]);

  useEffect(() => {
    if (countdown === null || countdown <= 0) {
      if (countdown === 0) setShowCompleted(false);
      return;
    }
    const timer = setTimeout(() => setCountdown((c) => (c !== null ? c - 1 : null)), 1000);
    return () => clearTimeout(timer);
  }, [countdown]);

  const handleClick = useCallback(() => {
    if (processingStatus === 'completed') {
      setCountdown(null);
      setShowCompleted(true);
    }
    if (currentCandidateId) {
      navigate(`/todo/interview/room/${currentCandidateId}?stage=${encodeURIComponent(currentStage)}`);
    }
  }, [currentCandidateId, currentStage, processingStatus, navigate]);

  const handleStop = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    recorderStore.stopRecording();
  }, [recorderStore]);

  const handleAbort = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    recorderStore.abortRecording();
  }, [recorderStore]);

  const handleExport = useCallback(() => {
    const ok = useRecorderStore.getState().exportLocalRecording();
    setExported(ok);
  }, []);

  const handleAcknowledge = useCallback(() => {
    useRecorderStore.getState().acknowledgeAlert();
  }, []);

  // 每次新的异常都要重新弹一次（确认过之后只保留顶部徽标）
  useEffect(() => {
    if (!uploadErrorKind) {
      setExported(false);
    }
  }, [uploadErrorKind]);

  useEffect(() => {
    // 有未上传音频时先让浏览器拦一道，避免误关页面导致本地唯一副本丢失
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      const state = useRecorderStore.getState();
      if (state.hasUnsavedAudio()) {
        e.preventDefault();
        e.returnValue = '录音尚未全部上传，离开页面会丢失这部分录音。';
        return e.returnValue;
      }
      if (state.isRecording || state.uploadingChunk) {
        state.abortRecording();
      }
      return undefined;
    };

    // pagehide 表示页面确实要走了，此时只能释放设备资源
    const handlePageHide = () => {
      const state = useRecorderStore.getState();
      if (state.isRecording || state.uploadingChunk) {
        state.abortRecording();
      }
    };

    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  // 判断是否应该显示
  const isProcessing = processingStatus !== 'idle' && processingStatus !== 'completed' && processingStatus !== 'failed';
  const shouldShow = isRecording || isProcessing || (processingStatus === 'completed' && showCompleted) || processingStatus === 'failed' || hasUploadError;

  if (!shouldShow) return null;

  // 状态信息
  const getStatusInfo = () => {
    // 🔴 上传异常判定必须在 isRecording 之前：
    // 通道已断时若仍显示"录音中"，面试官会毫不知情地把整场面试录进内存然后丢掉
    if (hasUploadError) {
      return {
        icon: <AlertCircle className="h-4 w-4" />,
        text: uploadErrorKind === 'auth' ? '登录已过期，录音未上传！' : `录音上传中断（${uploadFailures}次）`,
        color: 'bg-orange-500',
        shadow: '',
      };
    }
    if (isRecording) {
      return {
        icon: <Mic className="h-4 w-4" />,
        text: `录音中 ${formatDuration(duration)}`,
        color: 'bg-red-500',
        shadow: 'rgba(239, 68, 68, 0.4)',
      };
    }
    switch (processingStatus) {
      case 'uploading':
        return { icon: <Loader2 className="h-4 w-4 animate-spin" />, text: '上传中...', color: 'bg-orange-500', shadow: '' };
      case 'transcribing':
        return { icon: <Loader2 className="h-4 w-4 animate-spin" />, text: '转录中...', color: 'bg-blue-500', shadow: '' };
      case 'evaluating':
        return { icon: <Loader2 className="h-4 w-4 animate-spin" />, text: 'AI评价中...', color: 'bg-purple-500', shadow: '' };
      case 'completed':
        return {
          icon: <CheckCircle className="h-4 w-4" />,
          text: countdown !== null && countdown > 0 ? `评价完成 (${countdown}s)` : '评价完成',
          color: 'bg-green-500',
          shadow: '',
        };
      case 'failed':
        return { icon: <AlertCircle className="h-4 w-4" />, text: '处理失败', color: 'bg-red-600', shadow: '' };
      default:
        return { icon: <Loader2 className="h-4 w-4 animate-spin" />, text: '处理中...', color: 'bg-gray-500', shadow: '' };
    }
  };

  const statusInfo = getStatusInfo();

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key="global-recording-indicator"
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -50, transition: { duration: 0.3 } }}
        className="fixed top-4 right-4 z-50"
      >
        <motion.div
          className={`${statusInfo.color} text-white px-4 py-2.5 rounded-lg shadow-lg cursor-pointer flex items-center gap-3 min-w-[220px]`}
          onClick={handleClick}
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
          animate={
            isRecording && !hasUploadError
              ? {
                  boxShadow: [
                    `0 4px 20px ${statusInfo.shadow}`,
                    `0 4px 30px ${statusInfo.shadow}`,
                    `0 4px 20px ${statusInfo.shadow}`,
                  ],
                }
              : hasUploadError
              ? { opacity: [1, 0.6, 1] }
              : {}
          }
          transition={{
            duration: hasUploadError ? 1 : 1.5,
            repeat: isRecording || hasUploadError ? Infinity : 0,
            ease: 'easeInOut',
          }}
        >
          {/* 图标 */}
          <div>{statusInfo.icon}</div>

          {/* 信息 */}
          <div className="flex flex-col min-w-0">
            <div className="text-sm font-medium leading-tight">{statusInfo.text}</div>
            <div className="text-[11px] opacity-80 leading-tight truncate">
              {hasUploadError
                ? `已保住 ${formatDuration(savedDuration)}，之后未上传`
                : `${candidateName} · ${currentStage}`}
            </div>
          </div>

          {/* 录音波形动画：上传异常时停掉，避免"一切正常"的错觉 */}
          {isRecording && !hasUploadError && (
            <div className="flex items-center gap-0.5 ml-auto">
              {[1, 2, 3].map((i) => (
                <motion.div
                  key={i}
                  className="w-1 bg-white rounded-full"
                  animate={{ height: [4, 14, 4], opacity: [0.5, 1, 0.5] }}
                  transition={{ duration: 1, repeat: Infinity, delay: i * 0.12, ease: 'easeInOut' }}
                />
              ))}
            </div>
          )}

          {/* 停止按钮 */}
          {isRecording && (
            <>
              <button
                onClick={handleStop}
                className="ml-1 p-1 rounded hover:bg-white/20 transition-colors"
                title="结束录音并开始处理"
              >
                <Square className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={handleAbort}
                className="p-1 rounded hover:bg-white/20 transition-colors"
                title="强制终止录音"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </>
          )}

          {/* 点击提示 */}
          {!isRecording && (
            <div className="text-[11px] opacity-70 ml-auto whitespace-nowrap">点击查看</div>
          )}
        </motion.div>
      </motion.div>

      {/* 上传异常告警模态：右上角徽标在面试中极易被忽略，必须有一次强制确认 */}
      {showAlertModal && (
        <motion.div
          key="recording-upload-alert"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
        >
          <motion.div
            initial={{ scale: 0.95, y: 10 }}
            animate={{ scale: 1, y: 0 }}
            className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6"
          >
            <div className="flex items-start gap-3">
              <div className="mt-0.5 p-2 rounded-full bg-orange-100">
                <AlertCircle className="h-5 w-5 text-orange-600" />
              </div>
              <div className="min-w-0">
                <h3 className="text-base font-semibold text-gray-900">录音未能上传</h3>
                <div className="mt-2 text-sm text-gray-600 space-y-2">
                  {uploadErrorKind === 'auth' ? (
                    <p>
                      你的<span className="font-medium text-gray-900">登录凭证已过期</span>，
                      从 {formatDuration(savedDuration)} 之后的录音<span className="font-medium text-gray-900">没有上传成功</span>。
                      重新登录才能恢复上传。
                    </p>
                  ) : (
                    <p>
                      录音上传已<span className="font-medium text-gray-900">连续失败 {uploadFailures} 次</span>，
                      从 {formatDuration(savedDuration)} 之后的内容尚未上传成功。
                      {isRecording ? '系统仍在自动重试。' : '请导出录音保存到本地。'}
                    </p>
                  )}
                  <p className="text-orange-700 bg-orange-50 rounded-md px-3 py-2">
                    请勿关闭或刷新本页面，否则尚未上传的录音会丢失。
                    建议先点「导出录音到本地」保存一份。
                  </p>
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  <button
                    onClick={handleExport}
                    className="px-3 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors inline-flex items-center gap-1.5"
                    type="button"
                  >
                    <Download className="h-4 w-4" />
                    导出录音到本地
                  </button>
                  <button
                    onClick={handleAcknowledge}
                    className="px-3 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm font-medium hover:bg-gray-50 transition-colors"
                    type="button"
                  >
                    {isRecording ? '我已知晓，继续录音' : '我已知晓'}
                  </button>
                </div>
                {exported && (
                  <p className="mt-2 text-xs text-green-600">已导出到本地下载目录</p>
                )}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
